import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { authAPI } from '../services/api';

interface Permissions {
  admin_portal: boolean;
  mill_portal: boolean;
  store_portal: boolean;
  delivery_portal: boolean;
  delivery_manager: boolean;
  crm: boolean;
  crm_assignment: string;
  excluded_sections: string[];
  is_superuser: boolean;
}

interface User {
  id: number;
  username: string;
  email: string;
  role: string;
  first_name: string;
  last_name: string;
  display_name: string;
  crm_assignment: string;
  can_access_admin_portal: boolean;
  can_access_mill_portal: boolean;
  can_access_crm: boolean;
  can_access_store_portal: boolean;
  can_access_delivery_portal: boolean;
  can_manage_delivery: boolean;
  is_superuser?: boolean;
  is_staff?: boolean;
  permissions: Permissions;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  canAccessAdminPortal: boolean;
  canAccessMillPortal: boolean;
  canAccessCRM: boolean;
  canAccessStorePortal: boolean;
  canAccessDeliveryPortal: boolean;
  canManageDelivery: boolean;
  /** Why there is no user: 'network' (no answer - the person may well be signed in)
   *  or 'unauthenticated' (the server said no). The Delivery driver view opens its
   *  saved route on 'network'; everything else treats both as signed out. */
  authFailure: 'network' | 'unauthenticated' | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const DEFAULT_PERMISSIONS: Permissions = {
  admin_portal: false,
  mill_portal: false,
  store_portal: false,
  delivery_portal: false,
  delivery_manager: false,
  crm: false,
  crm_assignment: 'NONE',
  excluded_sections: [],
  is_superuser: false,
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authFailure, setAuthFailure] = useState<'network' | 'unauthenticated' | null>(null);

  useEffect(() => {
    // Check auth by calling /api/auth/me/ endpoint (cookies sent automatically)
    const checkAuth = async () => {
      // Timeout: if auth check takes >5s (e.g. Capacitor WebView cookie issues), bail out
      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Auth check timeout')), 5000)
      );

      try {
        const response = await Promise.race([authAPI.me(), timeout]);

        // SECURITY: Only allow staff users to access admin app
        if (!response.is_staff && !response.is_superuser) {
          setAuthFailure('unauthenticated');
          setUser(null);
          setLoading(false);
          return;
        }

        // Read permissions from backend response — single source of truth
        const perms: Permissions = response.permissions || DEFAULT_PERMISSIONS;

        const userData: User = {
          id: response.id,
          username: response.username || response.email || '',
          email: response.email,
          role: response.role || '',
          first_name: response.first_name || '',
          last_name: response.last_name || '',
          display_name: response.name || response.email,
          crm_assignment: perms.crm_assignment || 'NONE',
          can_access_admin_portal: perms.admin_portal,
          can_access_mill_portal: perms.mill_portal,
          can_access_crm: perms.crm,
          can_access_store_portal: perms.store_portal,
          can_access_delivery_portal: perms.delivery_portal,
          can_manage_delivery: perms.delivery_manager,
          is_superuser: response.is_superuser,
          is_staff: response.is_staff,
          permissions: perms,
        };

        setAuthFailure(null);
        setUser(userData);
      } catch (error) {
        // Timeout or 401 — clear user, show login. Record which: a 401/403 is the
        // server saying "signed out"; a timeout, no response or a 5xx is the network.
        const status = (error as { response?: { status?: number } })?.response?.status;
        setAuthFailure(status === 401 || status === 403 ? 'unauthenticated' : 'network');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    try {
      // Login endpoint sets httpOnly cookies automatically
      const response = await authAPI.login(username, password);

      // Read permissions from backend response — single source of truth
      const perms: Permissions = response.permissions || DEFAULT_PERMISSIONS;

      const userData: User = {
        id: response.id || response.user_id,
        username: response.username || response.email || '',
        email: response.email,
        role: response.role || '',
        first_name: response.first_name || '',
        last_name: response.last_name || '',
        display_name: response.display_name || response.name || response.email,
        crm_assignment: perms.crm_assignment || 'NONE',
        can_access_admin_portal: perms.admin_portal,
        can_access_mill_portal: perms.mill_portal,
        can_access_crm: perms.crm,
        can_access_store_portal: perms.store_portal,
        can_access_delivery_portal: perms.delivery_portal,
        can_manage_delivery: perms.delivery_manager,
        is_superuser: response.is_superuser,
        is_staff: response.is_staff,
        permissions: perms,
      };

      setAuthFailure(null);
      setUser(userData);
    } catch (error) {
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    // Call backend logout to clear cookies
    await authAPI.logout();
    setAuthFailure('unauthenticated');
    setUser(null);
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    canAccessAdminPortal: user?.can_access_admin_portal ?? false,
    canAccessMillPortal: user?.can_access_mill_portal ?? false,
    canAccessCRM: user?.can_access_crm ?? false,
    canAccessStorePortal: user?.can_access_store_portal ?? false,
    canAccessDeliveryPortal: user?.can_access_delivery_portal ?? false,
    canManageDelivery: user?.can_manage_delivery ?? false,
    authFailure,
  }), [user, loading, authFailure, login, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
